import apiClient from '../../shared/services/apiClient';

/**
 * User Service (User Version)
 *
 * Handles user's own profile and account management.
 * Users can manage their profile, password, preferences, and API keys.
 *
 * All routes use /api/user/* prefix
 */
class UserService {
  // ==========================================
  // Profile Management
  // ==========================================

  /**
   * Get current user profile
   */
  async getProfile() {
    try {
      const response = await apiClient.get('/api/user/profile');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Update user profile
   * @param {object} profileData - Profile data (name, email, etc.)
   */
  async updateProfile(profileData) {
    try {
      const response = await apiClient.put('/api/user/profile', profileData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Password Management
  // ==========================================

  /**
   * Change password
   * @param {string} currentPassword - Current password
   * @param {string} newPassword - New password
   */
  // emailCode: only for the first password of an OAuth-only account (see sendPasswordSetupCode)
  async changePassword(currentPassword, newPassword, emailCode) {
    try {
      const response = await apiClient.put('/api/user/change-password', {
        current_password: currentPassword,
        new_password: newPassword,
        ...(emailCode ? { email_code: emailCode } : {})
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Preferences
  // ==========================================

  /**
   * Get user preferences
   */
  async getPreferences() {
    try {
      const response = await apiClient.get('/api/user/preferences');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Update user preferences
   * @param {object} preferences - User preferences (language, theme, etc.)
   */
  async updatePreferences(preferences) {
    try {
      const response = await apiClient.put('/api/user/preferences', preferences);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // API Key Management
  // ==========================================

  /**
   * Get user API keys
   */
  async getApiKeys() {
    try {
      const response = await apiClient.get('/api/user/api-keys');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Generate new API key
   * @param {string} description - API key description
   */
  async generateApiKey(description, expiresInDays) {
    try {
      const response = await apiClient.post('/api/user/api-keys', { name: description, description, expires_in_days: expiresInDays });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Delete API key
   * @param {number} keyId - API key ID
   */
  async deleteApiKey(keyId) {
    try {
      const response = await apiClient.delete(`/api/user/api-keys/${keyId}`);
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Activity Log
  // ==========================================

  /**
   * Get user activity log
   * @param {object} params - Query parameters (page, limit, etc.)
   */
  async getActivity(params = {}) {
    try {
      const response = await apiClient.get('/api/user/activity', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  // ==========================================
  // Balance & Billing
  // ==========================================

  /**
   * Get user balance
   */
  async getBalance() {
    try {
      const response = await apiClient.get('/api/user/balance');
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }

  /**
   * Get balance history
   * @param {object} params - Query parameters (page, limit, etc.)
   */
  async getBalanceHistory(params = {}) {
    try {
      const response = await apiClient.get('/api/user/balance/history', { params });
      return response.data;
    } catch (error) {
      throw error.response?.data || error;
    }
  }
}

export default new UserService();
